import React, { useState } from 'react';

import CancelOrderModel from 'state/models/CancelOrder';
import Checkbox from 'components/forms/Checkbox';
import Input from 'components/forms/Input';
import Minus from 'assets/icons/Minus';
import Paragraph from 'components/text/Paragraph';
import Plus from 'assets/icons/Plus';
import PropTypes from 'prop-types';
import Radio from 'components/forms/Radio';
import TextLink from 'components/text/TextLink';
import ThemeButton from 'components/buttons/ThemeButton';
import colors from 'config/theme/colors';
import { getState } from 'state';
import regExp from 'utils/regExp';
import styled from 'libs/styled';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const orderNumberRegExp = /^\d{5}$/;
const articleNumberRegExp = /^(\d{5}|\d{7})$/;

const Form = styled('form')`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Fields = styled('div')`
    display: flex;
    flex-direction: column;

    > * + * {
        margin-top: 32px;
    }
`;

const RadioGroup = styled('div')`
    display: flex;
    flex-direction: column;
    margin-top: 32px;
`;

const GroupLabel = styled(Paragraph)`
    color: ${colors.grey6};
    font-size: 16px;
    margin: 0 0 8px 0;
`;

const ReturnItems = styled('div')`
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    padding-top: 16px;
`;

const ReturnItemsLabel = styled(Paragraph)`
    color: ${colors.grey6};
    font-size: 16px;
    margin: 0 0 16px 0;
`;

const Rows = styled('div')`
    display: flex;
    flex-direction: column;

    > * + * {
        margin-top: 16px;
    }
`;

const Row = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RowInner = styled('div')`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const RowBorder = styled('div')`
    width: 100%;
    height: 1px;
    background-color: ${colors.grey3};
`;

const ArticleInput = styled('input')`
    flex: 1 0 0;
    min-width: 0;
    border: none;
    background-color: transparent;
    padding: 0 0 8px 0;
    font-family: inherit;
    font-size: 18px;
    color: ${colors.black};

    :focus {
        outline: none;
    }

    ::placeholder {
        color: ${colors.grey6};
    }
`;

const Controls = styled('div')`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 16px;
`;

const StepButton = styled('button')`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: 1px solid ${colors.black};
    border-radius: 100px;
    background-color: transparent;
    cursor: pointer;
`;

const Quantity = styled('span')`
    font-size: 16px;
`;

const LinkButton = styled('button')`
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 18px;
    color: ${colors.black};
    text-decoration: underline;
    cursor: pointer;
`;

const AddRowButton = styled(LinkButton)`
    margin-top: 16px;
    align-self: flex-start;
`;

const Terms = styled('div')`
    margin-top: 32px;
`;

const SubmitWrapper = styled('div')`
    margin-top: 24px;
`;

const Message = styled(Paragraph)`
    margin: 0;
    font-size: 18px;
`;

const ErrorText = styled(Paragraph)`
    color: ${colors.error};
    font-size: 16px;
    margin: 8px 0 0 0;
`;

const createReturnItem = () => ({ articleNumber: '', quantity: 1 });

/**
 * Cancel order form ("Ångra köp").
 * Controlled-state form with client-side validation. On a valid submit the payload is posted
 * to the GrebbCommerce API (which forwards it to Rule.io) via the CancelOrder model.
 *
 * When "partial return" is selected an extra section appears where the customer can list the
 * article numbers (5 or 7 digits) of the products to return and adjust the quantity per row.
 *
 * @param {object} confirmationPage - Transformed link ({ to }) to redirect to after a successful submit.
 * @param {string} id - Unique id used as a prefix for field names.
 */

const CancelOrderForm = ({ confirmationPage, id = 'cancel-order' }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const deliveryStatusItems = [
        { id: 'no_delivery_confirmation', text: [t('cancel_order.delivery_status.no_confirmation')] },
        { id: 'delivery_confirmation', text: [t('cancel_order.delivery_status.has_confirmation')] },
        { id: 'unknown', text: [t('cancel_order.delivery_status.unknown')] },
    ];

    const returnTypeItems = [
        { id: 'full', text: [t('cancel_order.return_type.full')] },
        { id: 'partial', text: [t('cancel_order.return_type.partial')] },
    ];

    const [values, setValues] = useState({ firstName: '', lastName: '', email: '', orderNumber: '' });
    const [deliveryStatus, setDeliveryStatus] = useState(deliveryStatusItems[0].id);
    const [returnType, setReturnType] = useState(returnTypeItems[0].id);
    const [returnItems, setReturnItems] = useState([createReturnItem()]);
    const [termsChecked, setTermsChecked] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const isPartialReturn = returnType === 'partial';

    // Redirect to the CMS-configured confirmation page after a successful submit.
    // Mirrors the internal/external link handling used by components/base/Link.
    const redirectToConfirmation = to => {
        if (!to) {
            return false;
        }

        if (to.indexOf('/') !== 0) {
            if (typeof window !== 'undefined') {
                window.location.assign(to);
            }
            return true;
        }

        const path = getState('application')?.path || '';
        const href = path && to.indexOf(`${path}/`) !== 0 ? `${path}${to}` : to;
        history.push(href);
        return true;
    };

    const handleFieldChange = name => e => {
        // Read synchronously: React pools/nullifies the event before the functional updater runs.
        const { value } = e.target;
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const updateArticleNumber = (index, value) => {
        setReturnItems(prev => prev.map((item, i) => (i === index ? { ...item, articleNumber: value } : item)));
    };

    const changeQuantity = (index, delta) => {
        setReturnItems(prev =>
            prev.map((item, i) => (i === index ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item))
        );
    };

    const addRow = () => {
        setReturnItems(prev => [...prev, createReturnItem()]);
    };

    const removeRow = index => {
        setReturnItems(prev => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
        const newErrors = {};

        if (!values.firstName.trim()) {
            newErrors.firstName = { name: 'firstName', message: t('cancel_order.errors.first_name') };
        }

        if (!values.lastName.trim()) {
            newErrors.lastName = { name: 'lastName', message: t('cancel_order.errors.last_name') };
        }

        if (!regExp.validation.correctEmail.test(values.email.trim())) {
            newErrors.email = { name: 'email', message: t('cancel_order.errors.email') };
        }

        if (!orderNumberRegExp.test(values.orderNumber.trim())) {
            newErrors.orderNumber = { name: 'orderNumber', message: t('cancel_order.errors.order_number') };
        }

        if (isPartialReturn) {
            const filledItems = returnItems.filter(item => item.articleNumber.trim() !== '');

            if (filledItems.length === 0) {
                newErrors.returnItems = { message: t('cancel_order.errors.article_numbers_required') };
            } else if (!filledItems.every(item => articleNumberRegExp.test(item.articleNumber.trim()))) {
                newErrors.returnItems = { message: t('cancel_order.errors.article_number') };
            }
        }

        if (!termsChecked) {
            newErrors.terms = { name: 'terms', message: t('cancel_order.errors.terms') };
        }

        return newErrors;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        e.stopPropagation();

        const newErrors = validate();
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        const payload = {
            ...values, // firstName, lastName, email, orderNumber
            deliveryStatus,
            reason: returnType,
            termsAccepted: termsChecked,
        };

        // When a partial return is selected, normalize the article rows to a string:
        // one "- productNumber (quantity)" line per item.
        if (isPartialReturn) {
            payload.returnItems = returnItems
                .filter(item => item.articleNumber.trim() !== '')
                .map(item => `- ${item.articleNumber} (${item.quantity})`)
                .join('\n');
        }

        setSubmitError(false);
        setIsSubmitting(true);

        try {
            await CancelOrderModel.submit(payload);

            // Prefer the CMS confirmation page, otherwise show the inline success message.
            if (confirmationPage?.to && redirectToConfirmation(confirmationPage.to)) {
                return;
            }

            setSubmitted(true);
        } catch (err) {
            setSubmitError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return <Message>{t('cancel_order.form_success')}</Message>;
    }

    return (
        <Form noValidate onSubmit={handleSubmit}>
            <Fields>
                <Input
                    color="black"
                    error={errors.firstName}
                    handleChange={handleFieldChange('firstName')}
                    label={t('cancel_order.first_name')}
                    name={`${id}-first-name`}
                    required
                    type="text"
                />
                <Input
                    color="black"
                    error={errors.lastName}
                    handleChange={handleFieldChange('lastName')}
                    label={t('cancel_order.last_name')}
                    name={`${id}-last-name`}
                    required
                    type="text"
                />
                <Input
                    color="black"
                    error={errors.email}
                    handleChange={handleFieldChange('email')}
                    label={t('cancel_order.email')}
                    name={`${id}-email`}
                    required
                    type="email"
                />
                <Input
                    color="black"
                    error={errors.orderNumber}
                    handleChange={handleFieldChange('orderNumber')}
                    label={t('cancel_order.order_number')}
                    name={`${id}-order-number`}
                    required
                    type="text"
                />
            </Fields>

            <RadioGroup>
                <GroupLabel>{t('cancel_order.delivery_status.label')}</GroupLabel>
                <Radio
                    defaultActive={deliveryStatusItems[0].id}
                    handleChange={item => setDeliveryStatus(item.id)}
                    items={deliveryStatusItems}
                    name={`${id}-delivery-status`}
                />
            </RadioGroup>

            <RadioGroup>
                <GroupLabel>{t('cancel_order.return_type.label')}</GroupLabel>
                <Radio
                    defaultActive={returnTypeItems[0].id}
                    handleChange={item => setReturnType(item.id)}
                    items={returnTypeItems}
                    name={`${id}-return-type`}
                />

                {isPartialReturn && (
                    <ReturnItems>
                        <ReturnItemsLabel>{t('cancel_order.partial_return.label')}</ReturnItemsLabel>
                        <Rows>
                            {returnItems.map((item, index) => (
                                <Row key={index}>
                                    <RowInner>
                                        <ArticleInput
                                            aria-label={t('cancel_order.partial_return.placeholder')}
                                            inputMode="numeric"
                                            onChange={e => updateArticleNumber(index, e.target.value)}
                                            placeholder={t('cancel_order.partial_return.placeholder')}
                                            value={item.articleNumber}
                                        />
                                        <Controls>
                                            <StepButton
                                                aria-label={t('cancel_order.partial_return.decrease')}
                                                onClick={() => changeQuantity(index, -1)}
                                                type="button"
                                            >
                                                <Minus height="16px" width="16px" />
                                            </StepButton>
                                            <Quantity>{item.quantity}</Quantity>
                                            <StepButton
                                                aria-label={t('cancel_order.partial_return.increase')}
                                                onClick={() => changeQuantity(index, 1)}
                                                type="button"
                                            >
                                                <Plus height="16px" width="16px" />
                                            </StepButton>
                                            <LinkButton onClick={() => removeRow(index)} type="button">
                                                {t('cancel_order.partial_return.remove')}
                                            </LinkButton>
                                        </Controls>
                                    </RowInner>
                                    <RowBorder />
                                </Row>
                            ))}
                        </Rows>
                        <AddRowButton onClick={addRow} type="button">
                            {t('cancel_order.partial_return.add_row')}
                        </AddRowButton>
                        {errors.returnItems && <ErrorText>{errors.returnItems.message}</ErrorText>}
                    </ReturnItems>
                )}
            </RadioGroup>

            <Terms>
                <Checkbox
                    error={errors.terms}
                    handleChange={e => setTermsChecked(e.target.checked)}
                    label={
                        <span>
                            {`${t('cancel_order.accept_terms.text')} `}
                            <TextLink to={t('cancel_order.accept_terms.link_url')}>
                                {t('cancel_order.accept_terms.link_label')}
                            </TextLink>
                        </span>
                    }
                    name={`${id}-terms`}
                    required
                />
            </Terms>

            <SubmitWrapper>
                <ThemeButton
                    disabled={isSubmitting}
                    size="lg"
                    textAlign="center"
                    theme="black"
                    type="submit"
                    width="100%"
                >
                    {t('cancel_order.submit')}
                </ThemeButton>
                {submitError && <ErrorText>{t('cancel_order.form_error')}</ErrorText>}
            </SubmitWrapper>
        </Form>
    );
};

CancelOrderForm.propTypes = {
    confirmationPage: PropTypes.shape({
        label: PropTypes.string,
        to: PropTypes.string,
    }),
    id: PropTypes.string,
};

export default CancelOrderForm;
